/**
 * Environment Configuration
 * 
 * Centralized configuration for environment variables and application settings.
 * This module provides type-safe access to environment variables and handles
 * different environment configurations (development, staging, production).
 */

export interface AppConfig {
  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;
  
  // Environment Information
  appEnv: 'development' | 'staging' | 'production';
  appName: string;
  appVersion: string;
  isDebug: boolean;
  
  // File Upload Configuration
  maxFileSize: number;
  allowedFileTypes: string[];
  
  // UI Configuration
  ui: {
    defaultTheme: 'light' | 'dark';
    defaultLanguage: string;
    defaultDateFormat: string;
  };
  
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback?: string): string {
  const value = import.meta.env[key];
  if (value === undefined && fallback === undefined) {
    console.warn(`Environment variable ${key} is not defined`);
    return '';
  }
  return value || fallback || '';
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
}

/**
 * Get number environment variable
 */
function getNumberEnvVar(key: string, fallback: number): number {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Application configuration object
 */
export const config: AppConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', ''),
  apiTimeout: getNumberEnvVar('VITE_API_TIMEOUT', 60000),
  
  // Environment Information
  appEnv: (getEnvVar('VITE_APP_ENV', 'development') as AppConfig['appEnv']),
  appName: getEnvVar('VITE_APP_NAME', 'Appraisal 360'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  isDebug: getBooleanEnvVar('VITE_DEBUG', true),
  
  // File Upload Configuration
  maxFileSize: getNumberEnvVar('VITE_MAX_FILE_SIZE', 10485760), // 10MB
  allowedFileTypes: getEnvVar('VITE_ALLOWED_FILE_TYPES', 'image/jpeg,image/png,image/gif,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet').split(','),
  
  
  // UI Configuration
  ui: {
    defaultTheme: (getEnvVar('VITE_DEFAULT_THEME', 'light') as AppConfig['ui']['defaultTheme']),
    defaultLanguage: getEnvVar('VITE_DEFAULT_LANGUAGE', 'en'),
    defaultDateFormat: getEnvVar('VITE_DEFAULT_DATE_FORMAT', 'MM/DD/YYYY'),
  },
  
};

/**
 * Utility functions for environment checking
 */
export const isDevelopment = config.appEnv === 'development';
export const isStaging = config.appEnv === 'staging';
export const isProduction = config.appEnv === 'production';
export const isDebugMode = config.isDebug;

/**
 * Log configuration in development mode
 */
if (isDevelopment && isDebugMode) {
  console.group('🔧 Application Configuration');
  console.log('Environment:', config.appEnv);
  console.log('API Base URL:', config.apiBaseUrl);
  console.log('App Name:', config.appName);
  console.log('Debug Mode:', config.isDebug);
  console.log('🎯 API Target:', config.appEnv === 'staging' ? 'STAGING SERVER' : config.appEnv === 'production' ? 'PRODUCTION SERVER' : 'LOCAL SERVER');
  console.groupEnd();
}

export default config;
